import { type DropdownProps, DayPickerProps, DayPicker } from 'react-day-picker';
import { enGB, pl, ru, uk } from 'react-day-picker/locale';
import { type ChangeEvent } from 'react';

import { SelectContent, SelectTrigger, SelectValue, SelectItem, Select } from '../select/select';
import { buttonVariants } from '../button/button';
import { cn } from '../../utils';

function CalendarSelect({
  className,
  onChange,
  options,
  value,
}: Pick<DropdownProps, 'className' | 'onChange' | 'options' | 'value'>) {
  const onMonthSelected = (value: string) => {
    const event = {
      target: { value: +value },
    } as unknown as ChangeEvent<HTMLSelectElement>;

    onChange?.(event);
  };

  return (
    <Select onValueChange={onMonthSelected} value={value?.toString()}>
      <SelectTrigger className={cn('w-auto', className)}>
        <SelectValue placeholder="Select Date" />
      </SelectTrigger>
      <SelectContent>
        {options?.map((option, idx) => (
          <SelectItem
            value={option.value.toString()}
            key={`${idx}-${option.label}`}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export type CalendarProps = DayPickerProps;

const calendarLocales = { en: enGB, pl: pl, ru: ru, uk: uk };

function Calendar({
  showOutsideDays = false,
  classNames,
  className,
  lang,
  ...props
}: DayPickerProps) {
  const selectedLanguage = lang || localStorage.getItem('language') || 'pl';
  const locale = calendarLocales[selectedLanguage as keyof typeof calendarLocales];

  return (
    <DayPicker
      classNames={{
        months: 'flex flex-col sm:flex-row gap-y-4 sm:gap-x-4 sm:gap-y-0',
        month: 'flex flex-col gap-y-2',
        month_caption: 'flex',
        dropdowns: 'flex gap-2 max-w-25',
        dropdown: 'flex',
        caption_label: 'text-sm font-medium',
        nav: 'absolute right-3 top-4.5',
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100 z-10',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex space-x-1',
        weekday: 'text-weak rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full gap-1',
        weeks: 'flex flex-col gap-1',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 aria-selected:bg-surface-weak aria-selected:[&.day-outside]:bg-weak/50',
          props.mode === 'range'
            ? '[&.day-range-end]:rounded-r-md [&.day-range-start]:rounded-l-full aria-selected:[&.day-range-end]:rounded-r-full first:aria-selected:rounded-l-full last:aria-selected:rounded-r-full'
            : 'aria-selected:rounded-full',
        ),
        day_button: cn(buttonVariants({ variant: 'ghost' }), 'size-8 p-0 font-normal rounded-full'),
        range_start:
          'day-range-start before:absolute before:-right-0.5 before:top-0 before:w-0.5 before:bg-surface-weak before:bottom-0 [&.day-outside]:before:bg-weak/50',
        range_end:
          'day-range-end before:absolute [&.day-range-start]:before:bg-transparent before:-left-0.5 before:top-0 before:w-0.5 before:bg-surface-weak before:bottom-0 [&.day-outside]:before:bg-weak/50',
        selected:
          'day-selected *:bg-surface *:text-contrast *:hover:bg-surface *:hover:text-contrast *:focus:bg-surface *:focus:text-contrast *:opacity-100',
        today: '*:text-surface-strong-active',
        outside:
          'day-outside *:text-weak *:opacity-50 *:aria-selected:bg-weak/50 *:aria-selected:text-weak *:aria-selected:opacity-30',
        disabled: 'text-weak/30 opacity-50',
        range_middle:
          'day-range-middle *:aria-selected:bg-red-600 *:aria-selected:text-surface w-9 -mx-0.5 [&>button]:bg-transparent [&>button]:text-weak',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        // eslint-disable-next-line react/prop-types
        Dropdown: ({ onChange, options, value }) => {
          return (
            <CalendarSelect
              onChange={onChange}
              className="w-full"
              options={options}
              value={value}
            />
          );
        },
      }}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      startMonth={new Date(2024, 6)}
      endMonth={new Date(2025, 9)}
      defaultMonth={new Date()}
      captionLayout="dropdown"
      locale={locale}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
